import { useEffect, useState } from "react";

/**
 * The faces chart text is set in. They are separate families (see
 * `public/Fonts/fonts.css`), so each is loaded on its own.
 */
const CHART_FONT_FACES = ['12px "Inter"', '12px "Inter Bold"'] as const;

/** The longest a chart waits for its fonts before drawing with fallbacks. */
const CHART_FONT_WAIT_LIMIT_MS = 3000;

/** How often to look again for a face whose stylesheet has not arrived. */
const FACE_REGISTRATION_POLL_MS = 100;

/**
 * Loads one face. `load` resolves at once, with nothing, for a face no
 * stylesheet has registered yet, so this asks again until one has or the
 * deadline passes.
 */
async function loadRegisteredFace(
    fonts: FontFaceSet,
    face: string,
    deadline: number,
): Promise<void> {
    while ((await fonts.load(face)).length === 0 && Date.now() < deadline) {
        await new Promise((resolve) =>
            window.setTimeout(resolve, FACE_REGISTRATION_POLL_MS),
        );
    }
}

let fontsSettled = false;
let fontsReady: Promise<void> | undefined;

/**
 * Resolves once the chart fonts have loaded, or after the wait limit.
 *
 * Google Charts measures its labels when it draws. A chart drawn before a
 * webfont arrives lays out with a fallback face and has to redraw when the
 * font lands. `document.fonts.ready` alone is not enough: the faces are
 * registered by a stylesheet `WebFontLoader` injects, which can still be on its
 * way, so each face is loaded explicitly first.
 */
export function whenChartFontsReady(): Promise<void> {
    fontsReady ??= (async () => {
        const fonts =
            typeof document === "undefined" ? undefined : document.fonts;
        if (fonts) {
            const deadline = Date.now() + CHART_FONT_WAIT_LIMIT_MS;
            const loaded = Promise.all(
                CHART_FONT_FACES.map((face) =>
                    loadRegisteredFace(fonts, face, deadline),
                ),
            ).then(() => fonts.ready);
            const limit = new Promise((resolve) =>
                window.setTimeout(resolve, CHART_FONT_WAIT_LIMIT_MS),
            );
            await Promise.race([loaded, limit]).catch(() => undefined);
        }
        fontsSettled = true;
    })();
    return fontsReady;
}

/**
 * Whether a chart may draw. True from the first render once the fonts have
 * settled, so only charts on a cold load wait.
 */
export function useChartFontsReady(): boolean {
    const [ready, setReady] = useState(fontsSettled);

    useEffect(() => {
        if (ready) return;
        let active = true;
        void whenChartFontsReady().then(() => {
            if (active) setReady(true);
        });
        return () => {
            active = false;
        };
    }, [ready]);

    return ready;
}
